/**
 * Integration Partners Configuration (EVI-1098)
 *
 * Partner data (names, logos, colors, availability) is fetched from the
 * backend via GET /evidence/integrations?region=<iso>. This file provides:
 *
 * - The IntegrationPartnerConfig interface consumed by UI components
 * - A mapping helper from the backend IntegrationInfo schema
 * - A synchronous cache-based lookup for citation/card rendering
 */

import { defineMessage } from 'react-intl';
import { GET_EVIDENCE_INTEGRATIONS } from '@/hooks/query/integrations/evidence/useQueryEvidenceIntegrations';
import type { components } from '@heidi/schema/ml-scribe';
import { reactQueryClient } from '@/utils/reactQueryClient';

type IntegrationInfo = components['schemas']['IntegrationInfo'];
type IntegrationsListResponse = components['schemas']['IntegrationsListResponse'];

/**
 * Configuration for an integration partner, consumed by UI components.
 */
export interface IntegrationPartnerConfig {
  /** API identifier sent to backend (e.g., "dynamed") */
  id: string;
  /** Full display name (e.g., "DynaMed") */
  displayName: string;
  /** URL to logo asset */
  logo: string;
  /** Primary brand color hex for borders and accents */
  brandColor: string;
  /** When true, clicking this partner opens a setup flow instead of toggling (EVI-1093) */
  needsSetup?: boolean;
  /**
   * True when the partner has been configured AND ships an in-place edit
   * dialog (reusing the same dialog as first-time setup). Surfaces should
   * render an edit affordance that re-opens the setup dialog in edit mode.
   *
   * Only set for partners listed in `PARTNERS_WITH_SETUP_DIALOG` *and* with
   * an existing `setup_config` — i.e. the user has something to edit.
   */
  canEditSetup?: boolean;
  /** Backend-selected retrieval configuration for this publisher. */
  useTavily?: boolean;
}

/** Shown while a publisher is still web search, before the native integration replaces it. */
export const addingMoreJournalsDailyMessage = defineMessage({
  defaultMessage: 'Adding more journals daily',
  id: 'WdrvA1c9k5',
  description:
    'Footer under partner sources while journal publishers are still web search, before the native partner integration replaces them.',
});

/** Shown under each publisher that is still web search, next to a web icon. */
export const fullTextSearchComingSoonMessage = defineMessage({
  defaultMessage: 'Full-text search coming soon.',
  id: 'brpuyFN8bk',
  description:
    'Subtitle under a journal publisher in the partner sources list while it is searched on the web, before full-text search of its articles is available.',
});

export const hasWebSearchJournals = (
  partners: readonly Pick<IntegrationPartnerConfig, 'useTavily'>[]
): boolean => partners.some((partner) => partner.useTavily === true);

/** Default max results sent to the backend per partner. */
export const DEFAULT_PARTNER_MAX_RESULTS = 10;

/**
 * Square marks bundled with the app (EVI-2563).
 *
 * Catalog `logo_url`s for these partners were letter favicons, so the default
 * Sources stack rendered as the word "MEE" (and ClinicalKey reused Elsevier's
 * mark). The same files are published at `scribe.heidihealth.com/assets/partners`
 * for other clients; resolving them here keeps web, desktop, and on-prem on
 * the bundled copy even when the catalog URL is still the old favicon.
 *
 * EVI-2590: NEJM and Wiley block hotlinked favicons (403), so their partner
 * rows fell back to a letter tile; Cochrane's favicon is 16px. They reuse the
 * journal dock marks.
 */
const HOSTED_PARTNER_LOGOS: Readonly<Record<string, string>> = {
  mims: '/assets/partners/mims.png',
  mims_nz: '/assets/partners/mims.png',
  elsevier: '/assets/partners/elsevier.png',
  clinicalkey_ai: '/assets/partners/clinicalkey.png',
  nejm: '/images/journals/nejm.png',
  wiley: '/images/journals/wiley.png',
  cochrane: '/images/journals/cochrane.png',
};

export const resolveHostedPartnerLogo = (
  partnerKey: string,
  logoUrl: string
): string => HOSTED_PARTNER_LOGOS[partnerKey] ?? logoUrl;

/**
 * Partner keys that ship an in-place setup dialog (same dialog reused for
 * first-time setup *and* editing). Surfaces in the partner picker render an
 * edit affordance for these once `setup_config` exists.
 *
 * Today this is HealthPathways only; new partners with an inline edit dialog
 * should be added here and have their open atom wired in the submenu.
 */
const PARTNERS_WITH_SETUP_DIALOG: ReadonlySet<string> = new Set(['healthpathways']);

/**
 * Maps a backend IntegrationInfo entry to the UI-facing config shape.
 */
export function mapIntegrationToPartnerConfig(
  integration: IntegrationInfo
): IntegrationPartnerConfig {
  const canEditSetup =
    PARTNERS_WITH_SETUP_DIALOG.has(integration.key) && !!integration.setup_config
      ? true
      : undefined;

  return {
    id: integration.key,
    displayName: integration.name,
    logo: resolveHostedPartnerLogo(integration.key, integration.logo_url),
    brandColor: integration.brand_color,
    canEditSetup,
    ...(integration.use_tavily && { useTavily: true }),
  };
}

/**
 * Synchronous lookup for a partner config by ID.
 *
 * Reads from the TanStack Query cache populated by useQueryEvidenceIntegrations.
 * Used by CitationBadge, SourceCard, and ReferenceCard which need partner
 * branding during render and don't have direct access to the query hook.
 *
 * Returns undefined if the cache is empty or the partner is not found.
 */
export function getPartnerConfig(
  partnerId: string
): IntegrationPartnerConfig | undefined {
  // Read all cached entries (across all region keys)
  const cache = reactQueryClient.getQueriesData<IntegrationsListResponse>({
    queryKey: [GET_EVIDENCE_INTEGRATIONS],
  });

  for (const [, data] of cache) {
    if (!data?.integrations) continue;
    const match = data.integrations.find((i) => String(i.key) === partnerId);
    if (match) return mapIntegrationToPartnerConfig(match);
  }

  return undefined;
}
